import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type UpsellsListOutput = OutputRouter['upsells']['list']
export type UpsellItem = UpsellsListOutput['upsells'][number]
export type UpsellDetails = OutputRouter['upsells']['getById']
export type CreateUpsellOutput = OutputRouter['upsells']['create']
export type UpdateUpsellOutput = OutputRouter['upsells']['update']
export type DeleteUpsellsOutput = OutputRouter['upsells']['delete']

// Input types
export type ListUpsellsInput = InputRouter['upsells']['list']
export type GetUpsellInput = InputRouter['upsells']['getById']
export type CreateUpsellInput = InputRouter['upsells']['create']
export type UpdateUpsellInput = InputRouter['upsells']['update']
export type DeleteUpsellsInput = InputRouter['upsells']['delete']

// Query hooks
export const useUpsellsList = (
    params: ListUpsellsInput,
    options?: {
        enabled?: boolean
        refetchOnMount?: boolean | 'always'
        refetchOnWindowFocus?: boolean
    },
) => {
    const { enabled = true, ...otherOptions } = options || {}
    return trpc.upsells.list.useQuery(params, {
        enabled: enabled && !!params.storeId,
        ...otherOptions,
    })
}

export const useUpsell = (id: string, enabled = true) => {
    return trpc.upsells.getById.useQuery(
        { id },
        {
            enabled: enabled && !!id,
            retry: 2,
            staleTime: 30000,
        },
    )
}

// Mutation hooks
export const useCreateUpsell = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.upsells.create.useMutation({
        onSuccess: async () => {
            await utils.upsells.list.invalidate()
            toast({
                title: 'Success',
                description: 'Upsell created successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to create upsell',
                variant: 'destructive',
            })
        },
    })
}

export const useUpdateUpsell = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.upsells.update.useMutation({
        onSuccess: () => {
            utils.upsells.list.invalidate()
            utils.upsells.getById.invalidate()
            toast({
                title: 'Success',
                description: 'Upsell updated successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to update upsell',
                variant: 'destructive',
            })
        },
    })
}

export const useDeleteUpsells = () => {
    const { toast } = useToast()
    const utils = trpc.useUtils()

    return trpc.upsells.delete.useMutation({
        onSuccess: () => {
            utils.upsells.list.invalidate()
            toast({
                title: 'Success',
                description: 'Upsell(s) deleted successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message || 'Failed to delete upsell(s)',
                variant: 'destructive',
            })
        },
    })
}
